import { type ReactNode, useState } from 'react';

import { type ColorToken, HorizontalMenuTabs, MetricCard } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  type CoachCounts,
  type OverviewResponse,
} from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';

import { Box, type SxProps, type Theme, Typography } from '@mui/material';

import { type AnalyticsSection } from './analytics.anchors';
import { programLabel, stageLabel } from './analytics.labels';
import { OVERVIEW_WITH_COACHES, OVERVIEW_WITHOUT_COACHES } from './analytics.layouts';
import {
  dividerSx,
  mutedTextSx,
  pctChipSx,
  pctChipTextSx,
  rowLabelSx,
  tallCardSx,
  titleSx,
  withClickable,
} from './analytics.styles';
import { CardInfoIcon } from './AnalyticsCard';
import { AnalyticsCardGrid } from './AnalyticsCardGrid';
import { useAgentContext } from './OverviewCards.agentContext';

const { coachingAnalytics } = appStrings.modules;

type BreakdownRow = Readonly<{ id: string; label: string; value: number }>;

type CoverageRow = Readonly<{ id: string; label: string; pct: number }>;

export const EngagementMetricCard = ({
  overview,
  onClick,
}: Readonly<{ overview: OverviewResponse; onClick?: () => void }>) => (
  <MetricCard
    id="coaching-analytics-metric-engagement"
    label={coachingAnalytics.metrics.engagement}
    value={overview.recently_active.count}
    action={
      <CardInfoIcon
        info={[coachingAnalytics.metrics.engagementInfo]}
        title={coachingAnalytics.metrics.engagement}
      />
    }
    onClick={onClick}
    footer={
      <Typography variant="text-sm-regular" sx={mutedTextSx}>
        {coachingAnalytics.metrics.engagementSubtitle}
      </Typography>
    }
  />
);

export const CoachesMetricCard = ({
  counts,
  onClick,
}: Readonly<{ counts: CoachCounts; onClick?: () => void }>) => (
  <MetricCard
    id="coaching-analytics-metric-coaches"
    label={coachingAnalytics.metrics.coaches}
    value={counts.total}
    action={
      <CardInfoIcon
        info={[coachingAnalytics.metrics.coachesInfo]}
        title={coachingAnalytics.metrics.coaches}
      />
    }
    onClick={onClick}
    footer={
      <Box sx={pctChipSx}>
        <Typography variant="text-xs-medium" sx={pctChipTextSx}>
          {coachingAnalytics.metrics.backrsCoaches(counts.backrs)}
        </Typography>
      </Box>
    }
  />
);

const coverageRowsOf = (overview: OverviewResponse): readonly CoverageRow[] => {
  const { assessmentCoverage } = coachingAnalytics;
  const { completions } = overview;

  return [
    { id: 'blueprint', label: assessmentCoverage.blueprint, pct: completions.blueprint.percentage },
    {
      id: 'are-you-on-track',
      label: assessmentCoverage.areYouOnTrack,
      pct: completions.are_you_on_track.percentage,
    },
    {
      id: 'life-readiness',
      label: assessmentCoverage.lifeReadiness,
      pct: completions.life_readiness.percentage,
    },
  ];
};

/** The overview cards keyed by the area names the layouts place them under. */
export const overviewCards = (
  overview: OverviewResponse,
  onCardClick?: (section: AnalyticsSection) => void,
): Record<string, ReactNode> => ({
  students: (
    <StudentsBreakdownCard
      overview={overview}
      onClick={onCardClick && (() => onCardClick('overview'))}
    />
  ),
  engagement: (
    <EngagementMetricCard
      overview={overview}
      onClick={onCardClick && (() => onCardClick('engagement'))}
    />
  ),
  coaches: overview.coaches && (
    <CoachesMetricCard
      counts={overview.coaches}
      onClick={onCardClick && (() => onCardClick('coaching'))}
    />
  ),
  coverage: (
    <AssessmentCoverageCard
      rows={coverageRowsOf(overview)}
      onClick={onCardClick && (() => onCardClick('overview'))}
    />
  ),
});

export const AnalyticsOverviewGrid = ({
  overview,
  onCardClick,
}: Readonly<{
  overview: OverviewResponse;
  onCardClick?: (section: AnalyticsSection) => void;
}>) => {
  useAgentContext(overview);

  return (
    <AnalyticsCardGrid
      layout={overview.coaches ? OVERVIEW_WITH_COACHES : OVERVIEW_WITHOUT_COACHES}
      cards={overviewCards(overview, onCardClick)}
    />
  );
};

const BreakdownList = ({ rows }: Readonly<{ rows: readonly BreakdownRow[] }>) => (
  <Box sx={rowListSx}>
    {rows.map((row) => (
      <Box key={row.id} id={`coaching-analytics-breakdown-${row.id}`} sx={breakdownRowSx}>
        <Typography variant="text-md-medium" sx={rowLabelSx}>
          {row.label}
        </Typography>
        <Typography variant="text-md-semibold" sx={titleSx}>
          {row.value}
        </Typography>
      </Box>
    ))}
  </Box>
);

const StudentsBreakdownCard = ({
  overview,
  onClick,
}: Readonly<{ overview: OverviewResponse; onClick?: () => void }>) => {
  const { breakdown, metrics } = coachingAnalytics;
  const [tabIndex, setTabIndex] = useState(0);

  const source = tabIndex === 0 ? overview.students.by_stage : overview.students.by_program;
  const labelOf = tabIndex === 0 ? stageLabel : programLabel;
  const rows: readonly BreakdownRow[] = source.map((bucket) => ({
    id: bucket.id,
    label: labelOf(bucket.id),
    value: bucket.count,
  }));

  return (
    <Box
      id="coaching-analytics-card-students"
      onClick={onClick}
      sx={withClickable(tallCardSx, onClick)}
    >
      <Box sx={studentsHeaderSx}>
        <Box sx={cardHeaderSx}>
          <Typography variant="text-sm-medium" sx={mutedTextSx}>
            {metrics.students}
          </Typography>
          <CardInfoIcon info={[metrics.studentsInfo]} title={metrics.students} />
        </Box>
        <Box sx={studentsValueRowSx}>
          <Typography variant="display-sm-semibold" sx={titleSx}>
            {overview.students.total.toLocaleString()}
          </Typography>
        </Box>
      </Box>
      <Box sx={dividerSx} />
      <Box onClick={(event) => event.stopPropagation()}>
        <HorizontalMenuTabs
          id="coaching-analytics-breakdown-mode"
          variant="filter"
          tabs={[breakdown.byEnrollment, breakdown.byProgram]}
          value={tabIndex}
          setValue={setTabIndex}
          containerSxOverride={breakdownTabsSx}
        />
      </Box>
      <BreakdownList rows={rows} />
    </Box>
  );
};

const COVERAGE_FILL_COLORS: readonly ColorToken[] = ['Brand/200', 'Brand/400', 'Brand/600'];

/** Below this the badge cannot fit inside the fill, so it sits after the bar. */
const BADGE_INSIDE_FROM_PCT = 12;

const CoverageRowItem = ({
  row,
  fillColor,
}: Readonly<{ row: CoverageRow; fillColor: ColorToken }>) => {
  const isBadgeInside = row.pct >= BADGE_INSIDE_FROM_PCT;

  return (
    <Box id={`coaching-analytics-coverage-${row.id}`} sx={coverageRowSx}>
      <Typography variant="text-sm-medium" sx={rowLabelSx}>
        {row.label}
      </Typography>
      <Box sx={coverageTrackSx}>
        <Box
          sx={(theme) => ({
            ...coverageFillSx(),
            backgroundColor: theme.backrs.colors[fillColor],
          })}
          style={{ width: `${row.pct}%` }}
        />
        <Box
          sx={coverageBadgeSx}
          style={{
            left: `${row.pct}%`,
            transform: isBadgeInside ? 'translate(-100%, -50%)' : 'translate(0, -50%)',
          }}
        >
          <Typography variant="text-xs-medium" sx={coverageBadgeTextSx}>
            {`${row.pct}%`}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const AssessmentCoverageCard = ({
  rows,
  onClick,
}: Readonly<{ rows: readonly CoverageRow[]; onClick?: () => void }>) => (
  <Box
    id="coaching-analytics-card-assessment-coverage"
    onClick={onClick}
    sx={withClickable(tallCardSx, onClick)}
  >
    <Box sx={cardHeaderSx}>
      <Typography variant="text-lg-semibold" sx={titleSx}>
        {coachingAnalytics.assessmentCoverage.title}
      </Typography>
      <CardInfoIcon
        info={[coachingAnalytics.assessmentCoverage.info]}
        title={coachingAnalytics.assessmentCoverage.title}
      />
    </Box>
    <Box sx={dividerSx} />
    <Box sx={coverageListSx}>
      {rows.map((row, index) => (
        <CoverageRowItem
          key={row.id}
          row={row}
          fillColor={COVERAGE_FILL_COLORS[Math.min(index, COVERAGE_FILL_COLORS.length - 1)]}
        />
      ))}
    </Box>
  </Box>
);

const cardHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const studentsHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const studentsValueRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: backrs.spacing['spacing-md'],
});

const rowListSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
};

const breakdownRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
  minHeight: 48,
  padding: `${backrs.spacing['spacing-xl']} ${backrs.spacing['spacing-3xl']} ${backrs.spacing['spacing-xl']} 0`,
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  boxSizing: 'border-box',
});

const breakdownTabsSx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  borderRadius: backrs.radius['radius-md'],
  '& .MuiTab-root': {
    zIndex: 1,
  },
  '& .MuiTabs-indicator': {
    zIndex: 0,
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
    boxShadow: backrs.shadows['shadow-xs'],
    borderRadius: backrs.radius['radius-md'],
  },
});

const coverageListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  width: '100%',
});

const coverageRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const coverageTrackSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  width: '100%',
  height: 24,
  borderRadius: backrs.radius['radius-md'],
  backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-secondary-hover']}`,
  boxSizing: 'border-box',
  overflow: 'hidden',
});

const coverageFillSx = () => ({
  position: 'absolute' as const,
  left: 0,
  top: 0,
  bottom: 0,
});

const coverageBadgeSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  paddingTop: backrs.spacing['spacing-xxs'],
  paddingBottom: backrs.spacing['spacing-xxs'],
  paddingLeft: backrs.spacing['spacing-sm'],
  paddingRight: backrs.spacing['spacing-md'],
  borderRadius: backrs.radius['radius-sm'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-secondary-hover']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  boxShadow: backrs.shadows['shadow-xs'],
});

const coverageBadgeTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});
